import { isInlineSelectionEditWithinSection } from '@/components/tiptap/shared/inline-selection-edit/inline-edit-decorations';
import { SectionActionsDropdown } from '@/components/tiptap/shared/section-actions-dropdown';
import { copiedSectionAtom } from '@/components/tiptap/shared/section-atoms';
import { SectionCopyButton } from '@/components/tiptap/shared/section-copy-button';
import { getSectionDraftState } from '@/components/tiptap/shared/section-draft-state';
import { SectionEhrToolbar } from '@/components/tiptap/shared/section-ehr-toolbar';
import {
  isDraftSectionEmpty,
  useBlurEmptySection,
} from '@/components/tiptap/shared/use-blur-empty-section';
import {
  useEmphasisDisabledContext,
  useSectionEditorContext,
} from '@/components/tiptap/vMarkdown/editor-type';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { isSectionHeadingFocused } from '@heidi/editor/core';
import { useTranslations } from '@heidi/runtime/i18n';
import { NodeSelection } from '@tiptap/pm/state';
import {
  NodeViewContent,
  NodeViewWrapper,
  useEditorState,
  type NodeViewProps,
} from '@tiptap/react';
import { useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useId, useState } from 'react';

export const SectionNodeComponent = ({
  node,
  editor,
  getPos,
  updateAttributes,
}: NodeViewProps) => {
  const { t } = useTranslations();
  const { sectionTitle, headingEmpty, bodyEmpty, isDraftSection, isHeadingLocked } =
    getSectionDraftState(node);

  // Stable per-instance identifier for the copied-section atom. Using the
  // section title would collide when two sections share the same heading.
  const sectionId = useId();
  const copiedSection = useAtomValue(copiedSectionAtom);
  const isCopied = copiedSection === sectionId;
  // Owned here, not inside `SectionEhrToolbar`: `useBlurEmptySection` must see a
  // popup opening in the SAME commit as the blur it causes, so the setters go
  // straight to the popups rather than through a child effect.
  const [isLinkEhrOpen, setIsLinkEhrOpen] = useState(false);
  const [isMappingOpen, setIsMappingOpen] = useState(false);
  const [isSectionActionsOpen, setIsSectionActionsOpen] = useState(false);
  const isEhrToolbarOpen = isLinkEhrOpen || isMappingOpen;

  const isFocused = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e.isFocused) return false;
      const pos = getPos();
      if (typeof pos !== 'number') return false;
      const { from } = e.state.selection;
      return from > pos && from < pos + node.nodeSize;
    },
  });

  const hasInlineSelectionEdit = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const pos = getPos();
      if (typeof pos !== 'number') return false;
      return isInlineSelectionEditWithinSection(e, pos, node.nodeSize);
    },
  });

  // Read-only surfaces (version history, permission-gated notes) must not show
  // the empty-title placeholder; gated at the attr write, matching MDNE.
  const isEditable = useEditorState({
    editor,
    selector: ({ editor: e }) => e.isEditable,
  });
  const isHeadingFocused = useEditorState({
    editor,
    selector: ({ editor: e }) => isSectionHeadingFocused(e, getPos(), node),
  });

  const isToolbarVisible =
    isFocused || hasInlineSelectionEdit || isEhrToolbarOpen || isSectionActionsOpen;

  const editorContext = useSectionEditorContext();
  const emphasisDisabled = useEmphasisDisabledContext();

  const deleteSection = useCallback(() => {
    const pos = getPos();
    if (typeof pos !== 'number') return;

    const tr = editor.state.tr
      .setSelection(NodeSelection.create(editor.state.doc, pos))
      .deleteSelection();

    editor.view.dispatch(tr);
  }, [editor.state.doc, editor.state.tr, editor.view, getPos]);

  // Only the explicit "Delete section" action is a user delete. `deleteSection`
  // is also called by `useBlurEmptySection` to auto-remove abandoned empty
  // drafts, which must not count toward `section_deleted`.
  const handleUserDeleteSection = useCallback(() => {
    track({
      event_name: TrackingEventName.SectionDeleted,
      props: { editor_context: editorContext, editor_type: 'vmarkdown' },
      options: { amplitude: false },
    });
    deleteSection();
  }, [editorContext, deleteSection]);

  useEffect(() => {
    if (!isDraftSection || isDraftSectionEmpty(node)) {
      return;
    }

    updateAttributes({ isDraftSection: false });
  }, [isDraftSection, node, updateAttributes]);

  useBlurEmptySection({
    isDraftSection,
    isFocused,
    isLinkEhrOpen,
    isComputerUseNotePushMappingOpen: isMappingOpen,
    isSectionActionsOpen,
    node,
    onDelete: deleteSection,
  });

  return (
    <NodeViewWrapper
      as="section"
      data-testid="section-block"
      data-section-title={sectionTitle}
      data-draft-section={isDraftSection ? 'true' : undefined}
      data-heading-empty={headingEmpty ? 'true' : undefined}
      data-heading-focused={
        isHeadingFocused && isEditable && !isHeadingLocked ? 'true' : undefined
      }
      data-heading-locked={isHeadingLocked ? 'true' : undefined}
      data-body-empty={bodyEmpty ? 'true' : undefined}
      data-section-active={isToolbarVisible || isCopied ? 'true' : undefined}
      style={
        {
          '--section-heading-placeholder': `'${t('common.vMarkdown.section.headingPlaceholder')}'`,
          '--section-body-placeholder': `'${t('common.vMarkdown.section.bodyPlaceholder')}'`,
        } as React.CSSProperties
      }
      className={cn(
        'group relative rounded-md p-2 transition-colors duration-700 ease-in-out',
        isCopied ? 'bg-surface-pressed' : 'has-[button:hover]:bg-surface-hover',
        !isCopied && isToolbarVisible && 'bg-surface-hover'
      )}
    >
      <NodeViewContent />
      <SectionEhrToolbar
        node={node}
        isEditable={isEditable}
        isToolbarVisible={isToolbarVisible}
        linkEhrAriaLabel={t('common.vMarkdown.section.linkEhrSection')}
        onLinkEhrOpenChange={setIsLinkEhrOpen}
        onMappingOpenChange={setIsMappingOpen}
        updateAttributes={updateAttributes}
      />
      <div
        contentEditable={false}
        className={cn(
          'absolute end-2 top-2 flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
          isToolbarVisible && 'opacity-100'
        )}
      >
        <SectionCopyButton
          sectionNode={node}
          sectionId={sectionId}
          editor={editor}
          className="static opacity-100 transition-none"
          route="vmarkdown"
        />
        <SectionActionsDropdown
          onDeleteSection={handleUserDeleteSection}
          onOpenChange={setIsSectionActionsOpen}
          ariaLabel={t('common.vMarkdown.section.sectionActions')}
          deleteSectionLabel={t('common.vMarkdown.section.deleteSection')}
          sectionNode={node}
          sectionId={sectionId}
          editor={editor}
          getSectionPos={getPos}
          emphasisDisabled={emphasisDisabled}
        />
      </div>
    </NodeViewWrapper>
  );
};
